import classNames from "classnames";
import React, {
  ComponentProps,
  createContext,
  forwardRef,
  ThHTMLAttributes,
  useContext,
} from "react";
import { ArrowLightLine } from "components/icons/ArrowLightLine";

type TableContextType = {
  layout?: "table" | "flex" | "fixed";
  isLoading?: boolean;
  sticky?: boolean;
};

const TableContext = createContext<TableContextType>({
  layout: "table",
  isLoading: false,
  sticky: true,
});

const Head = (
  props: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLTableSectionElement>,
    HTMLTableSectionElement
  >
) => {
  const { sticky } = useContext(TableContext);

  return (
    <thead
      {...props}
      className={classNames(
        "t-bg-surface-lighter-grey t-text-text-30 t-border-0 t-border-solid t-border-neutral-0 t-border-b t-top-0 t-z-table-head " +
          props.className || "",
        {
          "t-sticky": sticky,
        }
      )}
    />
  );
};

type HeadCellProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLTableCellElement>,
  HTMLTableCellElement
> &
  ThHTMLAttributes<HTMLTableCellElement> & {
    isUpperCase?: boolean;
    showDivider?: boolean;
    size?: "small" | "regular" | "large";
  };

const HeadCell = ({
  isUpperCase = true,
  showDivider = false,
  size = "regular",
  ...rest
}: HeadCellProps) => {
  const children = showDivider ? (
    <span className="t-flex t-items-center t-gap-1">
      {rest.children}
      <HeadCellSeparator />
    </span>
  ) : (
    rest.children
  );

  return (
    <th
      {...rest}
      className={classNames(rest.className, "t-text-subtext-sm", {
        "t-uppercase": isUpperCase,
        "t-px-3 t-py-2.5": size === "regular",
        "t-py-2.5 t-px-1.5": size === "small",
      })}
    >
      {children}
    </th>
  );
};

const Row = forwardRef<
  HTMLTableRowElement,
  React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLTableRowElement>,
    HTMLTableRowElement
  > & { onRowClick?: () => void; disabled?: boolean; isRowSelected?: boolean }
>((props, ref) => {
  const { layout } = useContext(TableContext);
  const { disabled, ...otherProps } = props;

  return (
    <tr
      onClick={!disabled ? props.onRowClick : undefined}
      {...otherProps}
      ref={ref}
      className={classNames(
        "t-px-3 t-border-solid t-border-neutral-0 t-border-b t-border-0 t-text-body-sm",
        props.className || "",
        {
          "t-bg-purple--1": props.isRowSelected,
          "t-flex t-items-center": layout === "flex",
          "hover:t-bg-surface-lighter-grey t-cursor-pointer": Boolean(
            props.onRowClick && !disabled
          ),
          "t-opacity-50": disabled,
        }
      )}
    />
  );
});

const HeadRow = forwardRef<HTMLTableRowElement, ComponentProps<typeof Row>>(
  (props, ref) => {
    return <Row ref={ref} {...props} className={classNames(props.className)} />;
  }
);

const BodyWrapper = (
  props: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLTableSectionElement>,
    HTMLTableSectionElement
  >
) => <div className="t-overflow-auto" {...props} />;

const Body = (
  props: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLTableSectionElement>,
    HTMLTableSectionElement
  >
) => <tbody {...props}></tbody>;

const Container = (
  props: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLTableSectionElement>,
    HTMLTableSectionElement
  > &
    TableContextType
) => {
  const { className, ...rest } = props;

  return (
    <TableContext.Provider
      value={{
        layout: props.layout || "table",
        isLoading: props.isLoading,
        sticky: props.sticky,
      }}
    >
      <div
        className={classNames(
          "t-w-full t-overflow-x-auto t-overflow-y-auto t-rounded-tl-lg t-rounded-tr-lg",
          className
        )}
        {...rest}
      />
    </TableContext.Provider>
  );
};

type CellProps = React.PropsWithChildren<
  React.DetailedHTMLProps<
    React.TdHTMLAttributes<HTMLTableCellElement>,
    HTMLTableCellElement
  >
> & {
  size?: "small" | "regular" | "large";
};

const Cell = ({ children, size = "regular", ...props }: CellProps) => {
  const { isLoading } = useContext(TableContext);

  return (
    <td
      {...props}
      className={classNames(props.className, {
        "t-px-3 t-py-2.5": size === "regular",
        "t-py-2.5 t-px-1.5": size === "small",
      })}
    >
      {isLoading ? (
        <div className="t-bg-neutral-10 t-rounded-md t-my-2 t-h-5 t-animate-pulse"></div>
      ) : (
        children
      )}
    </td>
  );
};

const Content = ({
  className,
  ...props
}: React.DetailedHTMLProps<
  React.TableHTMLAttributes<HTMLTableElement>,
  HTMLTableElement
>) => {
  const { layout } = useContext(TableContext);
  return (
    <table
      className={classNames("t-w-full", className, {
        "t-table-fixed": layout === "fixed",
      })}
      {...props}
    />
  );
};

const HeadCellSort = ({
  nextSortOrder = "asc",
  isSorted,
}: {
  nextSortOrder?: "asc" | "desc" | false;
  isSorted?: "asc" | "desc" | false;
}) => {
  return (
    <span
      className={classNames(
        "group-hover:t-visible group-hover:t-opacity-100 t-transition-opacity t-text-text-30 ",
        {
          "t-invisible t-opacity-0 group-hover:t-flex ": !isSorted,
          "t-flex": isSorted,
          "t-rotate-0": nextSortOrder === "asc",
          "t-rotate-180": nextSortOrder !== "asc",
        }
      )}
    >
      <ArrowLightLine />
    </span>
  );
};

const HeadCellSeparator = () => {
  return (
    <span className="t-border t-border-solid t-border-neutral-20 t-border-t-0 t-border-b-0 t-border-l-0 t-h-6 t-ml-1" />
  );
};

const Table = {
  Container,
  Content,
  Body,
  BodyWrapper,
  Row,
  Head,
  HeadCell,
  Cell,
  HeadCellSort,
  HeadRow,
  HeadCellSeparator,
};

export default Table;
